import { privateApiClient, publicApiClient } from './apiClient';
import { storageKeys } from '../config/environment';

/**
 * Service utilisateurs / rôles — aligné mobile + contrat OAUTH (pos-service AuthClient, UserRoleRqsPayload).
 *
 * APIs (gateway /api/v1) :
 * - GET  /usr/user-roles?pge&lmt&sts&usr&rol&sol&org
 * - GET  /usr/roles?pge&lmt&sol&slg
 * - POST /usr/companies/{org}/add-role
 * - POST /usr/companies/{org}/edit-role/{userRoleId}
 * - DELETE /usr/companies/{org}/revoke-role/{userRoleId}
 * - GET  /exposed/v1/usr/solutions?code=POS → id solution (S-…-C-SOL)
 */

export const POS_SOLUTION_CODE = 'POS';

let cachedSolutionId = null;

/** Lecture synchrone du stockage local (peut être vide ou obsolète). */
export const getStoredSolutionId = () =>
    localStorage.getItem(storageKeys.solution) ||
    localStorage.getItem('netikash_solution') ||
    null;

/**
 * Résout l'ID solution POS (S-…-C-SOL) comme mobile App.js + pos-service hosts.solution-id.
 */
export const fetchPosSolutionId = async () => {
    if (cachedSolutionId) return cachedSolutionId;

    const stored = getStoredSolutionId();
    if (stored && stored.startsWith('S-')) {
        cachedSolutionId = stored;
        return stored;
    }

    const { data } = await publicApiClient.get('/usr/solutions', {
        params: { code: POS_SOLUTION_CODE },
    });

    if (!data?.id) {
        throw new Error('Solution POS introuvable.');
    }

    cachedSolutionId = data.id;
    localStorage.setItem(storageKeys.solution, data.id);
    return data.id;
};

/** ID solution pour les appels /usr/* (jamais la chaîne "POS" seule). */
export const resolveSolutionId = async () => {
    const stored = getStoredSolutionId();
    if (stored && stored.startsWith('S-')) {
        cachedSolutionId = stored;
        return stored;
    }
    return fetchPosSolutionId();
};

/** Filtre UI rôle → paramètre API `rol`. */
export const mapFilterRoleToApiRol = (filterRole) => {
    if (filterRole === 'caissier') return 'cashier';
    if (filterRole === 'admin') return 'admin';
    return 'all';
};

/** Filtre UI statut → paramètre API `sts`. */
export const mapFilterStatusToApiSts = (filterStatus) => {
    if (filterStatus === 'active') return 'active';
    if (filterStatus === 'declined') return 'declined';
    if (filterStatus === 'revoked') return 'revoked';
    if (filterStatus === 'pending') return 'pending';
    return 'all';
};

export const getUserRoleStatusLabel = (status) => {
    const labels = {
        pending: 'En attente',
        active: 'Actif',
        declined: 'Abandonné',
        revoked: 'Révoqué',
    };
    return labels[status] || status || '—';
};

export const getUserRoleStatusColorClass = (status) => {
    switch (status) {
        case 'pending':
            return 'bg-amber-100 text-amber-800 border-amber-200';
        case 'active':
            return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        case 'declined':
            return 'bg-rose-100 text-rose-800 border-rose-200';
        case 'revoked':
        default:
            return 'bg-slate-100 text-slate-700 border-slate-200';
    }
};

const toPhoneString = (phone) => {
    if (phone == null) return '';
    if (typeof phone === 'string') return phone;
    if (typeof phone === 'object' && (phone.fullNumber != null || phone.number != null)) {
        return phone.fullNumber || phone.number || '';
    }
    return '';
};

const toEmailString = (email) => {
    if (email == null) return '';
    if (typeof email === 'string') return email;
    if (typeof email === 'object') {
        // Certains backends renvoient { address, domain, verified, user_id, ... }
        if (email.address) return String(email.address);
        const address = email.localPart || email.user || email.username || '';
        const domain = email.domain || '';
        const joined = address && domain ? `${address}@${domain}` : address || domain;
        return joined ? String(joined) : '';
    }
    return '';
};

export const resolveRoleDisplayKey = (roleId, roleName) => {
    const id = String(roleId || '').toLowerCase();
    const name = String(roleName || '').toLowerCase();
    if (id === 'cashier' || name === 'cashier' || name.includes('caissier')) return 'caissier';
    if (id === 'admin' || name === 'admin' || name.includes('administr')) return 'admin';
    return id || 'other';
};

export const filterUserRolesForOrganization = (items, organizationId) => {
    if (!organizationId) return items || [];
    return (items || []).filter((ur) => {
        const orgId =
            ur.company?.id ??
            ur.company_id ??
            ur.organization?.id ??
            ur.org_id ??
            ur.org;
        if (orgId == null || orgId === '') return true;
        return String(orgId) === String(organizationId);
    });
};

export const dedupeUserRolesById = (items) => {
    const seen = new Set();
    return (items || []).filter((ur) => {
        if (!ur?.id) return false;
        if (seen.has(ur.id)) return false;
        seen.add(ur.id);
        return true;
    });
};

/** Corps POST add-role / edit-role (mobile NewUserRoleModal + company_id). */
export const buildUserRoleFormPayload = (
    organizationId,
    { user_id, role_id, solution_id, userRoleId }
) => ({
    user_id,
    role_id,
    solution_id,
    company_id: organizationId,
    ...(userRoleId ? { id: userRoleId } : {}),
});

export const mapUserRoleRecord = (ur) => {
    const rawPhone = ur.user?.phoneNumber ?? ur.user?.phone;
    const roleId = ur.role?.id || ur.role_id || '';
    const roleName = ur.role?.name || '';
    const roleKey = resolveRoleDisplayKey(roleId, roleName);

    return {
        id: ur.user?.id || ur.id,
        userRoleId: ur.id,
        name:
            ur.user?.profile?.completeName ||
            ur.user?.name ||
            ur.user?.fullName ||
            toPhoneString(rawPhone) ||
            'Utilisateur',
        email: toEmailString(ur.user?.email),
        phone: toPhoneString(rawPhone),
        roleId,
        roleName: roleName || roleId,
        role: roleKey,
        status: ur.status || 'pending',
        statusLabel: getUserRoleStatusLabel(ur.status || 'pending'),
        companyName: ur.company?.name || '',
        solutionName: ur.solution?.name || ur.solution?.code || '',
        lastLogin: ur.user?.lastLogin
            ? new Date(ur.user.lastLogin).toLocaleDateString('fr-FR')
            : '—',
        joinDate: ur.createdAt
            ? new Date(ur.createdAt).toLocaleDateString('fr-FR')
            : '—',
        permissions: ur.role?.permissions || [],
        rawData: ur,
    };
};

/**
 * Liste des rôles pour invitation (GET /usr/roles — mobile NewUserRoleModal).
 * @param {string} roleSlug - filtre `slg` (ex. "cashier" sur écran terminal)
 */
export const fetchRolesForInvitation = async (roleSlug = '') => {
    const sol = await resolveSolutionId();
    return fetchRoles({
        pge: 1,
        lmt: 10,
        sol,
        slg: roleSlug || '',
    });
};

/**
 * Liste user-roles d'une organisation (GET /usr/user-roles — mobile UserRoleComponent).
 */
export const fetchOrganizationUserRoles = async (organizationId, options = {}) => {
    const sol = await resolveSolutionId();
    const {
        pge = 1,
        lmt = 100,
        sts = 'all',
        usr = 'all',
        rol = 'all',
    } = options;

    return fetchUserRoles({
        pge,
        lmt,
        sts,
        usr,
        rol,
        sol,
        org: organizationId,
    });
};

export const fetchUserRoles = async (params = {}) => {
    const defaultParams = {
        pge: 1,
        lmt: 100,
        sts: 'all',
        usr: 'all',
        rol: 'all',
        ...params,
    };

    const { data } = await privateApiClient.get('/usr/user-roles', {
        params: defaultParams,
    });

    return {
        contents: data?.contents || [],
        nextPage: data?.nextPage ?? null,
        previousPage: data?.previousPage ?? null,
    };
};

export const revokeUserRole = async (organizationId, userRoleId) => {
    const { data } = await privateApiClient.delete(
        `/usr/companies/${organizationId}/revoke-role/${userRoleId}`
    );

    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la révocation du rôle');
    }

    return data;
};

export const fetchUsers = async (params = {}) => {
    const { data } = await privateApiClient.get('/usr/users', { params });
    return { contents: data?.contents || [] };
};

export const updateUserProfile = async (userId, profileData) => {
    const { data } = await privateApiClient.post(`/usr/users/${userId}/profile`, profileData);

    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la mise à jour du profil');
    }

    return data?.contents || data;
};

export const fetchUserSession = async () => {
    const { data } = await privateApiClient.get('/usr/users/session');

    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la récupération de la session');
    }

    return data?.contents || data;
};

/** POST /usr/companies/{org}/add-role */
export const addUserRole = async (organizationId, roleData) => {
    const payload =
        roleData.company_id != null
            ? roleData
            : buildUserRoleFormPayload(organizationId, roleData);

    const { data } = await privateApiClient.post(
        `/usr/companies/${organizationId}/add-role`,
        payload
    );

    if (data?.error) {
        throw new Error(data.message || "Erreur lors de l'ajout du rôle");
    }

    return data?.payload || data;
};

/** POST /usr/companies/{org}/edit-role/{userRoleId} */
export const editUserRole = async (organizationId, userRoleId, roleData) => {
    const payload = buildUserRoleFormPayload(organizationId, {
        ...roleData,
        userRoleId: userRoleId || roleData.id || roleData.userRoleId,
    });

    const { data } = await privateApiClient.post(
        `/usr/companies/${organizationId}/edit-role/${userRoleId}`,
        payload
    );

    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la modification du rôle');
    }

    return data?.payload || data;
};

/** GET /usr/roles — `sol` obligatoire (ID solution POS). */
export const fetchRoles = async (params = {}) => {
    const sol = params.sol ?? (await resolveSolutionId());
    const defaultParams = {
        pge: 1,
        lmt: 10,
        slg: '',
        ...params,
        sol,
    };

    const { data } = await privateApiClient.get('/usr/roles', {
        params: defaultParams,
    });

    return data?.contents || [];
};
